import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MOTIVO_CANCELACION,
  cancelarFacturaSchema,
  entradasOrdenadas,
  formatearMoneda,
  type EstadoFactura,
} from "@uirtus/shared";
import { useRef, useState } from "react";
import { Campo, Confirmacion, Entrada, Modal, Seleccion } from "../componentes/ui";
import { ErrorApi, OperacionEnProceso, api } from "../lib/api";

interface FacturaLista {
  id: string;
  serie: string | null;
  folio: number | null;
  estado: EstadoFactura;
  uuid: string | null;
  total: string;
  moneda: string;
  metodoPago: string;
  creadoAt: string;
  /** El PAC lo regresa al timbrar. No hay copia propia, a diferencia del XML. */
  pdfUrl: string | null;
  /** Lo que contesta el SAT mientras la cancelación sigue en proceso. */
  estatusCancelacionSat: string | null;
  cliente: { id: string; rfc: string; razonSocial: string };
}

const COLOR_ESTADO: Record<EstadoFactura, string> = {
  BORRADOR: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300",
  PENDIENTE_TIMBRE: "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
  TIMBRADA: "bg-emerald-100 text-emerald-800 dark:bg-emerald-950 dark:text-emerald-300",
  ERROR_TIMBRE: "bg-red-100 text-red-800 dark:bg-red-950 dark:text-red-300",
  CANCELACION_SOLICITADA: "bg-orange-100 text-orange-800 dark:bg-orange-950 dark:text-orange-300",
  CANCELADA: "bg-slate-200 text-slate-600 dark:bg-slate-800 dark:text-slate-400",
};

export function Facturas() {
  const cliente = useQueryClient();
  const [aviso, setAviso] = useState<
    { tipo: "proceso" | "error" | "ok"; texto: string } | null
  >(null);
  const [confirmarBaja, setConfirmarBaja] = useState<FacturaLista | null>(null);
  const [cancelando, setCancelando] = useState<FacturaLista | null>(null);
  const [motivoCancelacion, setMotivoCancelacion] = useState("02");
  const [uuidSustitucion, setUuidSustitucion] = useState("");
  const [erroresCancelacion, setErroresCancelacion] = useState<Record<string, string>>({});

  const { data, isLoading } = useQuery({
    queryKey: ["facturas"],
    queryFn: () => api<FacturaLista[]>("/facturas"),
    // Mientras haya facturas en PENDIENTE_TIMBRE o CANCELACION_SOLICITADA,
    // refrescar solo: los jobs de reconciliación las van a resolver y la
    // pantalla debe reflejarlo sin que el usuario recargue.
    refetchInterval: (query) =>
      query.state.data?.some(
        (f) => f.estado === "PENDIENTE_TIMBRE" || f.estado === "CANCELACION_SOLICITADA",
      )
        ? 15_000
        : false,
  });

  /**
   * Una clave de idempotencia por factura, estable entre reintentos.
   *
   * Esto es lo que evita el doble timbrado: si el usuario da doble click, o
   * el navegador reintenta tras un timeout, sale la MISMA clave y el backend
   * reconoce que es la misma operación. La clave solo se descarta cuando el
   * resultado es definitivo — nunca tras un 202.
   */
  const claves = useRef(new Map<string, string>());
  const claveDe = (id: string): string => {
    const existente = claves.current.get(id);
    if (existente) return existente;
    const nueva = crypto.randomUUID();
    claves.current.set(id, nueva);
    return nueva;
  };

  const timbrar = useMutation({
    mutationFn: (id: string) =>
      api<FacturaLista>(`/facturas/${id}/timbrar`, {
        method: "POST",
        claveIdempotencia: claveDe(id),
      }),
    onSuccess: (_datos, id) => {
      claves.current.delete(id);
      setAviso({ tipo: "ok", texto: "Factura timbrada" });
      void cliente.invalidateQueries({ queryKey: ["facturas"] });
    },
    onError: (err, id) => {
      if (err instanceof OperacionEnProceso) {
        // La clave se CONSERVA a propósito. Si el usuario insiste, la misma
        // clave le devuelve el mismo resultado en vez de generar otro CFDI.
        setAviso({
          tipo: "proceso",
          texto:
            "El PAC no respondió. La factura quedó en proceso y se resolverá sola en un par de minutos. No vuelvas a timbrar.",
        });
        void cliente.invalidateQueries({ queryKey: ["facturas"] });
        return;
      }

      // Rechazo definitivo: se descarta la clave para que el siguiente
      // intento sea una operación nueva y explícita.
      claves.current.delete(id);
      setAviso({
        tipo: "error",
        texto:
          err instanceof ErrorApi ? err.message : "No se pudo timbrar la factura",
      });
    },
  });

  /**
   * Solo aplica a BORRADOR/ERROR_TIMBRE (el backend lo exige de todos
   * modos). Es la salida para una factura que rechazó el SAT: se borra y se
   * corrige el dato mal capturado en el cliente antes de crear otra — un
   * receptor ya timbrado es una "foto" congelada, corregir el cliente no
   * cambia una factura que ya existe.
   */
  const eliminar = useMutation({
    mutationFn: (id: string) => api<void>(`/facturas/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      setConfirmarBaja(null);
      void cliente.invalidateQueries({ queryKey: ["facturas"] });
    },
    onError: (err) => {
      setConfirmarBaja(null);
      setAviso({
        tipo: "error",
        texto: err instanceof ErrorApi ? err.message : "No se pudo eliminar la factura",
      });
    },
  });

  /**
   * El XML no se enlaza directo: `xmlPath` es nuestra copia en Storage, no
   * una URL pública. Cada clic pide una URL firmada de vida corta y la baja
   * con un <a download> creado al vuelo — no un window.open, que un
   * bloqueador de popups (frecuente en un panel de administración) tira.
   */
  const descargarXml = useMutation({
    mutationFn: (id: string) => api<{ url: string }>(`/facturas/${id}/xml`),
    onError: (err) =>
      setAviso({
        tipo: "error",
        texto: err instanceof ErrorApi ? err.message : "No se pudo obtener el XML",
      }),
  });

  const cerrarCancelacion = () => {
    setCancelando(null);
    setMotivoCancelacion("02");
    setUuidSustitucion("");
    setErroresCancelacion({});
  };

  /**
   * Igual que el timbrado: "solicitud aceptada" no es "cancelada". El SAT
   * puede dejarla en proceso hasta 72 horas si el receptor tiene que dar su
   * visto bueno, así que aquí solo se manda la solicitud — el job
   * `verificar-cancelaciones` (cada 15 min) es el que la resuelve de verdad.
   */
  const cancelar = useMutation({
    mutationFn: (vars: { id: string; motivo: string; uuidSustitucion?: string }) =>
      api<FacturaLista>(`/facturas/${vars.id}/cancelar`, {
        method: "POST",
        body: { motivo: vars.motivo, uuidSustitucion: vars.uuidSustitucion },
      }),
    onSuccess: () => {
      cerrarCancelacion();
      setAviso({
        tipo: "proceso",
        texto:
          "Cancelación enviada al SAT. Puede tardar hasta unos minutos en confirmarse; la pantalla se actualiza sola.",
      });
      void cliente.invalidateQueries({ queryKey: ["facturas"] });
    },
    onError: (err) =>
      setAviso({
        tipo: "error",
        texto: err instanceof ErrorApi ? err.message : "No se pudo solicitar la cancelación",
      }),
  });

  function enviarCancelacion() {
    if (!cancelando) return;
    const datos = {
      motivo: motivoCancelacion,
      uuidSustitucion: uuidSustitucion.trim() || undefined,
    };
    const resultado = cancelarFacturaSchema.safeParse(datos);
    if (!resultado.success) {
      const porCampo: Record<string, string> = {};
      for (const issue of resultado.error.issues) {
        const campo = String(issue.path[0] ?? "");
        if (campo && !porCampo[campo]) porCampo[campo] = issue.message;
      }
      setErroresCancelacion(porCampo);
      return;
    }
    cancelar.mutate({ id: cancelando.id, ...resultado.data });
  }

  if (isLoading) return <p className="text-sm text-slate-500">Cargando…</p>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Facturas</h1>
      </div>

      {aviso && (
        <div
          className={
            aviso.tipo === "proceso"
              ? "rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200"
              : aviso.tipo === "error"
                ? "rounded-md border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900 dark:border-red-800 dark:bg-red-950 dark:text-red-200"
                : "rounded-md border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-900 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200"
          }
        >
          {aviso.texto}
        </div>
      )}

      <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-900">
        <table className="w-full text-sm">
          <thead className="border-b border-slate-200 text-left text-xs uppercase text-slate-500 dark:border-slate-800">
            <tr>
              <th className="px-4 py-2">Folio</th>
              <th className="px-4 py-2">Cliente</th>
              <th className="px-4 py-2">Estado</th>
              <th className="px-4 py-2 text-right">Total</th>
              <th className="px-4 py-2"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {(data ?? []).map((f) => (
              <tr key={f.id}>
                <td className="px-4 py-2 font-mono text-xs">
                  {f.serie && f.folio !== null ? `${f.serie}-${f.folio}` : "—"}
                </td>
                <td className="px-4 py-2">
                  <div>{f.cliente.razonSocial}</div>
                  <div className="font-mono text-xs text-slate-500">
                    {f.cliente.rfc}
                  </div>
                </td>
                <td className="px-4 py-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-xs font-medium ${COLOR_ESTADO[f.estado]}`}
                  >
                    {f.estado.replace(/_/g, " ").toLowerCase()}
                  </span>
                </td>
                <td className="px-4 py-2 text-right font-mono">
                  {formatearMoneda(f.total, f.moneda)}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-2">
                    {(f.estado === "BORRADOR" || f.estado === "ERROR_TIMBRE") && (
                      <>
                        <button
                          onClick={() => timbrar.mutate(f.id)}
                          disabled={timbrar.isPending}
                          className="rounded-md bg-marca-600 px-3 py-1 text-xs font-medium text-white hover:bg-marca-700 disabled:opacity-50"
                        >
                          {timbrar.isPending && timbrar.variables === f.id
                            ? "Timbrando…"
                            : "Timbrar"}
                        </button>
                        <button
                          onClick={() => setConfirmarBaja(f)}
                          disabled={timbrar.isPending}
                          className="rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                        >
                          Eliminar
                        </button>
                      </>
                    )}
                    {f.estado === "PENDIENTE_TIMBRE" && (
                      <span className="text-xs text-amber-600">
                        resolviéndose…
                      </span>
                    )}
                    {f.estado === "CANCELACION_SOLICITADA" && (
                      <span
                        className="text-xs text-orange-600"
                        title={f.estatusCancelacionSat ?? undefined}
                      >
                        cancelación en proceso…
                      </span>
                    )}
                    {(f.estado === "TIMBRADA" || f.estado === "CANCELADA") && (
                      <>
                        {f.pdfUrl && (
                          <a
                            href={f.pdfUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                          >
                            PDF
                          </a>
                        )}
                        <button
                          onClick={() =>
                            descargarXml.mutate(f.id, {
                              onSuccess: (d) => {
                                const enlace = document.createElement("a");
                                enlace.href = d.url;
                                enlace.download =
                                  f.serie && f.folio !== null
                                    ? `${f.serie}-${f.folio}.xml`
                                    : `${f.id}.xml`;
                                document.body.appendChild(enlace);
                                enlace.click();
                                enlace.remove();
                              },
                            })
                          }
                          disabled={descargarXml.isPending && descargarXml.variables === f.id}
                          className="rounded-md border border-slate-300 px-3 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
                        >
                          {descargarXml.isPending && descargarXml.variables === f.id
                            ? "Un momento…"
                            : "XML"}
                        </button>
                      </>
                    )}
                    {f.estado === "TIMBRADA" && (
                      <button
                        onClick={() => setCancelando(f)}
                        className="rounded-md border border-red-300 px-3 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:border-red-800 dark:text-red-400 dark:hover:bg-red-950"
                      >
                        Cancelar
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {(data ?? []).length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-500">
                  Todavía no hay facturas.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <Confirmacion
        abierto={!!confirmarBaja}
        titulo="Eliminar factura"
        mensaje={
          <>
            Se va a borrar la factura de{" "}
            <strong>{confirmarBaja?.cliente.razonSocial}</strong>. Solo es
            posible porque no se ha timbrado — si el rechazo fue por un dato
            del cliente (nombre, régimen, código postal), corrígelo antes de
            crear la siguiente factura.
          </>
        }
        textoConfirmar="Eliminar"
        peligroso
        ocupado={eliminar.isPending}
        alCancelar={() => setConfirmarBaja(null)}
        alConfirmar={() => confirmarBaja && eliminar.mutate(confirmarBaja.id)}
      />

      <Modal
        titulo="Cancelar factura"
        abierto={!!cancelando}
        alCerrar={cerrarCancelacion}
        ancho="max-w-md"
      >
        <div className="space-y-4 p-5">
          <p className="text-sm text-slate-600 dark:text-slate-300">
            Se le pide al SAT cancelar la factura de{" "}
            <strong>{cancelando?.cliente.razonSocial}</strong>. No es
            inmediato: si el receptor tiene que dar su visto bueno, el SAT
            puede tardar hasta 72 horas — la pantalla se va a actualizar sola
            cuando se resuelva.
          </p>

          <Campo etiqueta="Motivo" requerido error={erroresCancelacion["motivo"]}>
            <Seleccion
              value={motivoCancelacion}
              onChange={(e) => setMotivoCancelacion(e.target.value)}
            >
              {entradasOrdenadas(MOTIVO_CANCELACION).map(([clave, nombre]) => (
                <option key={clave} value={clave}>
                  {clave} · {nombre}
                </option>
              ))}
            </Seleccion>
          </Campo>

          {motivoCancelacion === "01" && (
            <Campo
              etiqueta="UUID que sustituye a esta factura"
              requerido
              error={erroresCancelacion["uuidSustitucion"]}
              ayuda="El motivo 01 lo exige el SAT: es el folio fiscal del CFDI que corrige a este."
            >
              <Entrada
                value={uuidSustitucion}
                onChange={(e) => setUuidSustitucion(e.target.value)}
                placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
                error={!!erroresCancelacion["uuidSustitucion"]}
              />
            </Campo>
          )}

          <div className="flex justify-end gap-2 border-t border-slate-200 pt-4 dark:border-slate-800">
            <button
              onClick={cerrarCancelacion}
              className="rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:text-slate-200 dark:hover:bg-slate-800"
            >
              Cerrar
            </button>
            <button
              onClick={enviarCancelacion}
              disabled={cancelar.isPending}
              className="rounded-md border border-red-300 bg-white px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-red-800 dark:bg-slate-900 dark:text-red-400 dark:hover:bg-red-950"
            >
              {cancelar.isPending ? "Enviando…" : "Solicitar cancelación"}
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
